import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import {
    Box, Button, Card, CircularProgress, Collapse, IconButton, MenuItem, Table, TableBody,
    TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField, Typography,
} from '@material-ui/core';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import GetAppIcon from '@material-ui/icons/GetApp';

import {
    getSurvey, listSurveyResponses, exportSurveyResponses, downloadCsv, fmtDateTime,
} from '../../services/surveys.service';

/**
 * A survey's responses: newest first, filtered by version and date, each expandable to
 * its answers. Answers are shown with the wording of the version they were given in.
 *
 * Export: every matching response as CSV, one row each — with option codes (stable
 * across versions, best for analysis) or with labels (easier to read).
 *
 * Material-UI v4, so styling is `style=` rather than `sx=`.
 */

const fmtDuration = (ms) => {
    if (!ms) return '—';
    const s = Math.round(ms / 1000);
    return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${s % 60}s`;
};

const AnswerList = ({ answers }) => (
    <Table size="small">
        <TableBody>
            {answers.map((a, i) => (
                <TableRow key={`${a.q}-${a.row || i}`}>
                    <TableCell style={{ width: 70, fontFamily: 'monospace', color: '#888', verticalAlign: 'top' }}>{a.q}</TableCell>
                    <TableCell style={{ width: '38%', verticalAlign: 'top', color: '#555' }}>
                        {a.question}
                        {a.row && <div style={{ fontWeight: 600, color: '#333' }}>{a.row_label}</div>}
                    </TableCell>
                    <TableCell style={{ verticalAlign: 'top' }}>
                        {a.fields ? (
                            a.fields.map((fd) => (
                                <div key={fd.key}><span style={{ color: '#888' }}>{fd.label}:</span> {fd.value}</div>
                            ))
                        ) : a.row ? (
                            <>
                                {a.statement && <div>{a.statement}</div>}
                                {a.rating && <div style={{ color: '#666' }}>Rating: {a.rating}</div>}
                            </>
                        ) : (
                            <>
                                {a.ranked
                                    ? <ol style={{ margin: 0, paddingLeft: 18 }}>{a.answers.map((x) => <li key={x}>{x}</li>)}</ol>
                                    : a.answers.map((x) => <div key={x}>{x}</div>)}
                                {a.other_text && <div style={{ color: '#666', marginTop: 4 }}>Other: “{a.other_text}”</div>}
                                {a.extra_text && <div style={{ color: '#666', marginTop: 4 }}>Comment: “{a.extra_text}”</div>}
                            </>
                        )}
                    </TableCell>
                </TableRow>
            ))}
        </TableBody>
    </Table>
);

const ResponseRow = ({ r }) => {
    const [open, setOpen] = useState(false);
    return (
        <>
            <TableRow hover onClick={() => setOpen(!open)} style={{ cursor: 'pointer' }}>
                <TableCell padding="checkbox">
                    <IconButton size="small" aria-label="Show answers">{open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}</IconButton>
                </TableCell>
                <TableCell>{fmtDateTime(r.submitted_at)}</TableCell>
                <TableCell>v{r.survey_version}</TableCell>
                <TableCell>{r.context.programme || '—'}</TableCell>
                <TableCell>{fmtDuration(r.context.duration_ms)}</TableCell>
                <TableCell align="right">{new Set(r.answers.map((a) => a.q)).size}</TableCell>
            </TableRow>
            <TableRow>
                <TableCell colSpan={6} style={{ padding: 0, borderBottom: open ? undefined : 'none' }}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box style={{ padding: '8px 16px 16px', background: '#fafafa' }}>
                            <AnswerList answers={r.answers} />
                            <Typography variant="caption" style={{ color: '#aaa' }}>Response {r.id}</Typography>
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
};

const SurveyResponses = () => {
    const { survey_code } = useParams();
    const history = useHistory();

    const [title, setTitle] = useState(survey_code);
    const [versions, setVersions] = useState([]);
    const [version, setVersion] = useState('');
    const [from, setFrom] = useState('');
    const [to, setTo] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState('');
    const [error, setError] = useState('');

    // Version choices and the title.
    useEffect(() => {
        getSurvey(survey_code)
            .then((res) => {
                const d = res && res.data;
                if (!d) return;
                setTitle((d.live && d.live.definition.title) || survey_code);
                setVersions(d.versions.filter((v) => v.status === 'published'));
            })
            .catch(() => { /* the list below shows the error */ });
    }, [survey_code]);

    // An out-of-order response must not overwrite a newer one.
    const reqId = useRef(0);
    const filters = { survey_code, version: version || undefined, from: from || undefined, to: to || undefined };

    const load = useCallback(async () => {
        const mine = ++reqId.current;
        setLoading(true);
        setError('');
        try {
            const res = await listSurveyResponses({ ...filters, page: page + 1, limit: rowsPerPage });
            if (mine !== reqId.current) return;
            setRows((res && res.data && res.data.responses) || []);
            setTotal((res && res.data && res.data.total) || 0);
        } catch (e) {
            if (mine !== reqId.current) return;
            setRows([]);
            setTotal(0);
            setError((e && e.message) || 'Could not load responses.');
        } finally {
            if (mine === reqId.current) setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [survey_code, version, from, to, page, rowsPerPage]);

    useEffect(() => { load(); }, [load]);

    const onFilter = (setter) => (e) => { setter(e.target.value); setPage(0); };
    const onRowsPerPage = (e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); };

    const exportCsv = async (labels) => {
        setExporting(labels ? 'labels' : 'codes');
        setError('');
        try {
            const res = await exportSurveyResponses({ ...filters, labels: labels ? 'true' : undefined });
            downloadCsv(res.data.filename, res.data.csv);
        } catch (e) {
            setError((e && e.message) || 'Could not export.');
        } finally {
            setExporting('');
        }
    };

    return (
        <Box style={{ padding: 24 }}>
            <Button size="small" startIcon={<ArrowBackIcon />} onClick={() => history.push('/surveys/list')} style={{ marginBottom: 8 }}>
                All surveys
            </Button>
            <Typography variant="h5" style={{ fontWeight: 700 }}>Responses</Typography>
            <Typography variant="body2" style={{ color: '#666', marginBottom: 20 }}>
                {title} · each answer is shown with the wording of the version it was given in. From v4,
                responses include the respondent&rsquo;s name, school, age, gender and country: personal data, often of minors.
            </Typography>

            <Card style={{ padding: 16 }}>
                <Box style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', marginBottom: 16 }}>
                    <TextField select label="Version" value={version} onChange={onFilter(setVersion)} variant="outlined" size="small" style={{ width: 160 }}>
                        <MenuItem value="">All versions</MenuItem>
                        {versions.map((v) => <MenuItem key={v.version} value={String(v.version)}>v{v.version} ({v.responses})</MenuItem>)}
                    </TextField>
                    <TextField label="From" type="date" value={from} onChange={onFilter(setFrom)} variant="outlined" size="small" InputLabelProps={{ shrink: true }} />
                    <TextField label="To" type="date" value={to} onChange={onFilter(setTo)} variant="outlined" size="small" InputLabelProps={{ shrink: true }} />
                    <Box style={{ flex: 1 }} />
                    <Button variant="outlined" startIcon={<GetAppIcon />} disabled={!!exporting || !total} onClick={() => exportCsv(false)}>
                        {exporting === 'codes' ? 'Exporting…' : 'CSV (codes)'}
                    </Button>
                    <Button variant="outlined" startIcon={<GetAppIcon />} disabled={!!exporting || !total} onClick={() => exportCsv(true)}>
                        {exporting === 'labels' ? 'Exporting…' : 'CSV (labels)'}
                    </Button>
                </Box>

                {error && <Typography color="error" style={{ marginBottom: 12 }}>{error}</Typography>}

                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell padding="checkbox" />
                                <TableCell>Submitted</TableCell>
                                <TableCell>Version</TableCell>
                                <TableCell>Programme</TableCell>
                                <TableCell>Time taken</TableCell>
                                <TableCell align="right">Questions answered</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading && (
                                <TableRow><TableCell colSpan={6} style={{ textAlign: 'center', padding: 32 }}><CircularProgress size={24} /></TableCell></TableRow>
                            )}
                            {!loading && rows.length === 0 && (
                                <TableRow><TableCell colSpan={6} style={{ color: '#888' }}>
                                    {version || from || to ? 'No responses match these filters.' : 'No responses yet.'}
                                </TableCell></TableRow>
                            )}
                            {!loading && rows.map((r) => <ResponseRow key={r.id} r={r} />)}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div"
                    count={total}
                    page={page}
                    // v4 names (onChangePage) and the newer ones, as on the CPC requests page.
                    onChangePage={(e, p) => setPage(p)}
                    onPageChange={(e, p) => setPage(p)}
                    rowsPerPage={rowsPerPage}
                    onChangeRowsPerPage={onRowsPerPage}
                    onRowsPerPageChange={onRowsPerPage}
                    rowsPerPageOptions={[25, 50, 100]}
                />
            </Card>
        </Box>
    );
};

export default SurveyResponses;
